import { format } from 'date-fns';
import { type FC } from 'react';
import styled from 'styled-components';

import { useTexts } from '@/hooks/useTexts';
import { useUserDateFnsLocale, useUserLocale } from '@/i18n/hooks';
import { useOrderPageContext } from '@/pages/Order/OrderPageContext';
import { WithdrawalOrder } from '@/pages/Order/types/Order';
import { formatNumber } from '@/util/formatNumber';

import { useDiscretionaryPortfolios } from '../../OrderDetails/hooks/useDiscretionaryPortfolios';
import { createPortfolioBankAccountLabel } from '../../OrderDetails/utils/createPortfolioBankAccountLabel';
import { expectedTransferDate } from '../../OrderDetails/utils/expectedTransferDate';
import { MainHeaderRow } from './MainHeaderRow';
import { PortfolioRow } from './PortfolioRow';
import { Row } from './Row';

export const WithdrawalOrderDetailsTable: FC = () => {
  const texts = useTexts();
  const locale = useUserLocale();
  const dateFnsLocale = useUserDateFnsLocale();

  const context = useOrderPageContext<WithdrawalOrder>();
  const currentOrder = context.currentOrder!;

  const [portfolio] = useDiscretionaryPortfolios().filter(
    (portfolio) => portfolio.id === currentOrder.portfolioId,
  );

  const [portfolioBankAccount] = portfolio.portfolioBankAccounts.filter(
    (account) =>
      account.accountNumber === currentOrder.fromPortfolioBankAccountNumber,
  );

  return (
    <StyledTable
      cellSpacing="0"
      cellPadding="0"
      data-test="withdrawal-order-details"
    >
      <thead>
        <MainHeaderRow title={texts.orders.form.table.orderDetails} />
      </thead>

      <tbody>
        <PortfolioRow portfolioShortName={currentOrder.portfolioId} />

        <Row
          left={texts.orders.form.table.portfolioBankAccount}
          right={createPortfolioBankAccountLabel(portfolioBankAccount)}
        />

        <Row
          left={texts.orders.form.table.externalBankAccount}
          right={currentOrder.externalBankAccount}
        />

        <Row
          left={texts.orders.form.table.amount}
          right={formatNumber(
            currentOrder.amount,
            locale,
            currentOrder.currency,
          )}
        />

        <Row
          left={texts.orders.form.table.date}
          right={format(
            currentOrder.expectedTransferDate,
            expectedTransferDate.dateFormat(dateFnsLocale),
          )}
        />

        <Row
          left={texts.orders.form.table.messageToSam}
          right={
            currentOrder.messageToSam?.trim() ||
            texts.orders.form.table.messageNotSpecified
          }
        />
      </tbody>
    </StyledTable>
  );
};

const StyledTable = styled.table`
  line-height: 16px;
  width: 100%;
`;
